import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { Flex } from 'foundations-components/components/Flex';
import { findToolbarFieldsForFocusedField } from './utils';
import { TOOL_REGISTRY } from './tools/registry';
import { jsx as _jsx } from "react/jsx-runtime";
export default function FieldSettingsToolbar({
  moduleSchema,
  fieldOverrides,
  focusedField,
  onFieldChange,
  onToolbarInteractionStart,
  onToolbarInteractionEnd,
  onClose,
  toolContext
}) {
  const toolbarFields = useMemo(() => {
    if (!(moduleSchema !== null && moduleSchema !== void 0 && moduleSchema.fields)) {
      return [];
    }
    return findToolbarFieldsForFocusedField(moduleSchema.fields, fieldOverrides || {}, focusedField.path);
  }, [moduleSchema === null || moduleSchema === void 0 ? void 0 : moduleSchema.fields, fieldOverrides, focusedField.path]);
  const handleFieldChange = useCallback((path, value) => {
    onFieldChange(path, value, {}, {
      inlineEdited: false
    });
  }, [onFieldChange]);
  const onToolbarInteractionEndRef = useRef(onToolbarInteractionEnd);
  onToolbarInteractionEndRef.current = onToolbarInteractionEnd;
  const toolbarRef = useRef(null);
  const isListenerActiveRef = useRef(false);
  useEffect(() => {
    return () => {
      var _onToolbarInteraction;
      (_onToolbarInteraction = onToolbarInteractionEndRef.current) === null || _onToolbarInteraction === void 0 || _onToolbarInteraction.call(onToolbarInteractionEndRef);
    };
  }, []);
  const handleClickOutside = useCallback(e => {
    var _toolbarRef$current;
    if (!isListenerActiveRef.current) {
      return;
    }
    const target = e.target;
    const isInsideToolbar = (_toolbarRef$current = toolbarRef.current) === null || _toolbarRef$current === void 0 ? void 0 : _toolbarRef$current.contains(target);
    const isInsideFocusedElement = focusedField.domNode.contains(target);
    const isInsideDropdownMenu = target instanceof Element && target.closest('[data-dropdown-menu]');
    if (!isInsideToolbar && !isInsideFocusedElement && !isInsideDropdownMenu) {
      onClose();
    }
  }, [focusedField.domNode, onClose]);
  useEffect(() => {
    isListenerActiveRef.current = false;

    // Activate after next animation frame to skip the opening click
    const rafId = requestAnimationFrame(() => {
      isListenerActiveRef.current = true;
    });
    document.addEventListener('click', handleClickOutside);
    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [handleClickOutside]);
  const handleMouseDown = useCallback(() => {
    onToolbarInteractionStart === null || onToolbarInteractionStart === void 0 || onToolbarInteractionStart();
  }, [onToolbarInteractionStart]);
  const handleMouseUp = useCallback(() => {
    onToolbarInteractionEnd === null || onToolbarInteractionEnd === void 0 || onToolbarInteractionEnd();
  }, [onToolbarInteractionEnd]);
  if (toolbarFields.length === 0) {
    return null;
  }
  return /*#__PURE__*/_jsx(Popover, {
    target: focusedField.domNode,
    open: true,
    placement: "top right",
    arrowSize: "none",
    autoPlacement: true,
    padding: "100"
    // Wrapper div needed because Flex doesn't forward refs or accept data-* attributes
    ,
    content: /*#__PURE__*/_jsx("div", {
      ref: toolbarRef,
      "data-field-settings-toolbar": "true",
      children: /*#__PURE__*/_jsx(Flex, {
        flexWrap: "nowrap",
        alignItems: "center",
        gap: "100",
        onMouseDown: handleMouseDown,
        onMouseUp: handleMouseUp,
        children: toolbarFields.map(info => {
          const config = TOOL_REGISTRY[info.toolType];
          if (!config) return null;
          return /*#__PURE__*/_jsx(config.Component, {
            info: Object.assign({}, info, {
              toolContext
            }),
            config: config,
            onFieldChange: handleFieldChange,
            domNode: focusedField.domNode,
            onClose: onClose
          }, `${info.field.name}-${info.toolType}`);
        })
      })
    })
  });
}